import type * as CSS from 'csstype'
import { system, Config, get } from '@styled-system/core'
import { ResponsiveValue, TLength } from '../utils/types'

type StyleNumber = number

export interface TypographyProps {
  fontFamily?: ResponsiveValue<CSS.FontFamilyProperty>
  /**
   * The fontSize utility parses a component's `fontSize` prop and converts it into a CSS font-size declaration.
   *
   * - Numbers from 0-8 (or `theme.fontSizes.length`) are converted to values on the [font size scale](#default-theme).
   * - Numbers greater than `theme.fontSizes.length` are converted to raw pixel values.
   * - String values are passed as raw CSS values.
   * - And array values are converted into responsive values.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-family)
   */
  fontSize?: ResponsiveValue<CSS.FontSizeProperty<TLength>>
  /**
   * The font-style CSS property specifies whether a font should be styled with a normal, italic,
   * or oblique face from its font-family.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-style)
   */
  fontStyle?: ResponsiveValue<CSS.FontStyleProperty>
  /**
   * The font-weight CSS property specifies the weight (or boldness) of the font.
   *
   * The font weights available to you will depend on the font-family you are using. Some fonts are only available in normal and bold.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight)
   */
  fontWeight?: ResponsiveValue<CSS.FontWeightProperty>
  /**
   * The line-height CSS property sets the amount of space used for lines, such as in text. On block-level elements,
   * it specifies the minimum height of line boxes within the element.
   *
   * On non-replaced inline elements, it specifies the height that is used to calculate line box height.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/line-height)
   */
  lineHeight?: ResponsiveValue<CSS.LineHeightProperty<TLength>>
  /**
   * The text-align CSS property specifies the horizontal alignment of an inline or table-cell box.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-align)
   */
  textAlign?: ResponsiveValue<CSS.TextAlignProperty>
  /**
   * The color utility parses a component's `color` and `bg` props and converts them into CSS declarations.
   * By default the raw value of the prop is returned.
   *
   * Color palettes can be configured with the ThemeProvider to use keys as prop values, with support for dot notation.
   * Array values are converted into responsive values.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/color)
   */
  color?: ResponsiveValue<CSS.ColorProperty>
  /**
   * The letter-spacing CSS property sets the spacing behavior between text characters.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/letter-spacing)
   */
  letterSpacing?: ResponsiveValue<CSS.LetterSpacingProperty<TLength>>
  /**
   * The text-decoration-line CSS property sets the kind of decoration that is used on text in an element, such as an underline or overline.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-line)
   */
  textDecorationLine?: ResponsiveValue<CSS.TextDecorationLineProperty>
  /**
   * The header fontSize utility parses a component's `header` prop and converts it into a CSS font-size declaration.
   *
   * - Numbers from 0-8 (or `theme.headerSizes.length`) are converted to values on the [header size scale](#default-theme).
   * - Numbers greater than `theme.headerSizes.length` are converted to raw pixel values.
   * - String values are passed as raw CSS values.
   * - And array values are converted into responsive values.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-family)
   */
  header?: ResponsiveValue<CSS.FontSizeProperty<TLength>>
  /**
   * The header fontSize utility parses a component's `header` prop and converts it into a CSS font-size declaration.
   *
   * - Numbers from 0-8 (or `theme.headerSizes.length`) are converted to values on the [header size scale](#default-theme).
   * - Numbers greater than `theme.headerSizes.length` are converted to raw pixel values.
   * - String values are passed as raw CSS values.
   * - And array values are converted into responsive values.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-family)
   */
  h?: ResponsiveValue<CSS.FontSizeProperty<TLength>>
  /**
   * The text-shadow CSS property adds shadows to text. It accepts a comma-separated list of shadows to be applied to the text and any of its decorations. Each shadow is described by some combination of X and Y offsets from the element, blur radius, and color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-shadow)
   */
  textShadowStyle?: ResponsiveValue<CSS.TextShadowProperty>
  /**
   * The text-shadow CSS property adds shadows to text.  Each shadow is described by some combination of X and Y offsets from the element, blur radius, and color.  This field sets the X and Y offsets
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-shadow)
   */
  textShadowOffset?: { x: number; y: number }
  /**
   * The text-shadow CSS property adds shadows to text.  Each shadow is described by some combination of X and Y offsets from the element, blur radius, and color.  This field sets the blur radius.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-shadow)
   */
  textShadowRadius?: StyleNumber
  /**
   * The text-shadow CSS property adds shadows to text. Each shadow is described by some combination of X and Y offsets from the element, blur radius, and color.  This field sets the color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-shadow)
   */
  textShadowColor?: CSS.Color
}

export const textShadowStyle: any = (
  props: Partial<TypographyProps & { theme: any }>
) => {
  if (
    props.textShadowOffset &&
    props.textShadowOffset.x &&
    props.textShadowOffset.x
  ) {
    const textShadowOffsetX =
      (props.textShadowOffset && props.textShadowOffset.x) || '1px'
    const textShadowOffsetY =
      (props.textShadowOffset && props.textShadowOffset.y) || '1px'
    const textShadowRadius =
      (props.textShadowRadius && props.textShadowRadius) || '0'
    const textShadowColor =
      props.textShadowColor &&
      get(
        props.theme,
        ['colors', props.textShadowColor].join('.'),
        props.textShadowColor
      )

    return {
      textShadow: `${textShadowOffsetX} ${textShadowOffsetY} ${textShadowRadius} ${textShadowColor}`
    }
  }
  return null
}

const config: Config = {
  fontFamily: {
    property: 'fontFamily',
    scale: 'fonts'
  },
  fontSize: {
    property: 'fontSize',
    scale: 'fontSizes'
  },
  fontStyle: true,
  fontWeight: {
    property: 'fontWeight',
    scale: 'fontWeights'
  },
  lineHeight: {
    property: 'lineHeight',
    scale: 'lineHeights'
  },
  textAlign: true,
  color: {
    property: 'color',
    scale: 'colors'
  },
  letterSpacing: {
    property: 'letterSpacing',
    scale: 'letterSpacings'
  },
  textDecorationLine: true,
  header: {
    property: 'fontSize',
    scale: 'headerSizes',
    transform: (d, scale) => getValue(d - 1, scale),
    defaultScale: [32, 24, 20, 16, 12, 8]
  },
  textShadowStyle
}

config.h = config.header

export const typographyProps = system(config)

const getValue = (n: any, scale: any) => get(scale, n, n)
